import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Plus,
  Eye,
  Download,
  Pencil,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../api/axios";
import { LETTER_TYPES } from "../../utils/letterTemplates";
import {
  T,
  Page,
  PageHeader,
  Card,
  PrimaryButton,
  SecondaryButton,
  SearchInput,
  Select,
  IconButton,
  Pagination,
  EmptyState,
  StatusBadge,
  ModalOverlay,
} from "../../components/em/ui";

const PER_PAGE = 12;

const GenerateLetter = () => {
  const navigate = useNavigate();
  const basePath =
    (localStorage.getItem("role") || "").toLowerCase().trim() === "hr"
      ? "/hr"
      : "/admin";

  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [page, setPage] = useState(1);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchLetters();
  }, []);

  const fetchLetters = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/generated-letters");
      const rows = Array.isArray(data) ? data : [];
      setLetters(
        rows.map((r) => ({
          ...r,
          placeholder_data:
            r.placeholder_data && typeof r.placeholder_data === "string"
              ? JSON.parse(r.placeholder_data)
              : r.placeholder_data || {},
        })),
      );
    } catch (err) {
      console.error("Error fetching generated letters:", err);
      toast.error("Could not load letters");
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = (type) =>
    LETTER_TYPES.find((t) => t.value === type)?.label || type || "—";

  const employeeName = (l) =>
    l.placeholder_data?.employee_name ||
    l.placeholder_data?.employeeName ||
    "—";

  // Edit → re-open the generator prefilled with this letter's type + values so
  // the user can adjust and regenerate (letters are immutable, so this makes a
  // fresh one rather than mutating the original).
  const editLetter = (l) =>
    navigate(`${basePath}/generateletter/create`, {
      state: {
        prefill: {
          document_type: l.template_type,
          preview_values: l.placeholder_data || {},
        },
      },
    });

  // Download the stored PDF as a blob (auth header travels with the axios call).
  const downloadLetter = async (l) => {
    setDownloadingId(l.id);
    try {
      const res = await api.get(
        `/settings/generated-letters/${l.id}/download`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const name = `${typeLabel(l.template_type)}${
        employeeName(l) !== "—" ? ` - ${employeeName(l)}` : ""
      }.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = name.replace(/[/\\]+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading letter:", err);
      toast.error(
        err?.response?.status === 404
          ? "No PDF available for this letter"
          : "Failed to download letter",
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteLetter = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/settings/generated-letters/${confirmDelete.id}`);
      setLetters((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      toast.success("Letter deleted");
      setConfirmDelete(null);
    } catch (err) {
      console.error("Error deleting letter:", err);
      toast.error("Failed to delete letter");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-GB", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return letters.filter((l) => {
      if (filterType !== "all" && l.template_type !== filterType) return false;
      if (!q) return true;
      return (
        l.subject?.toLowerCase().includes(q) ||
        employeeName(l).toLowerCase().includes(q) ||
        String(l.id).includes(q)
      );
    });
  }, [letters, query, filterType]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageRows = filtered.slice(
    (safePage - 1) * PER_PAGE,
    safePage * PER_PAGE,
  );

  const th = {
    textAlign: "left",
    padding: "11px 16px",
    font: `600 11px/1.4 ${T.font}`,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: T.fg5,
    borderBottom: `1px solid ${T.border}`,
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "12px 16px",
    font: `400 13px/1.4 ${T.font}`,
    color: T.fg1,
    borderBottom: `1px solid ${T.border}`,
    verticalAlign: "middle",
  };

  return (
    <Page>
      <PageHeader
        icon={FileText}
        title="Letter Generator"
        subtitle="Generate, preview and download HR letters"
        actions={
          <PrimaryButton
            icon={Plus}
            onClick={() => navigate(`${basePath}/generateletter/create`)}
          >
            Create New
          </PrimaryButton>
        }
      />

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ width: 300, maxWidth: "100%" }}>
          <SearchInput
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name, subject or ID…"
          />
        </div>
        <div style={{ flex: 1 }} />
        <Select
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by letter type"
          style={{ width: "auto", minWidth: 190 }}
        >
          <option value="all">All letter types</option>
          {LETTER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <IconButton icon={RefreshCw} title="Refresh" onClick={fetchLetters} />
      </div>

      <Card padding={0} style={{ overflow: "hidden" }}>
        {loading ? (
          <div
            style={{
              padding: "56px 24px",
              textAlign: "center",
              font: `400 13px ${T.font}`,
              color: T.fg5,
            }}
          >
            Loading letters…
          </div>
        ) : pageRows.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={
              query || filterType !== "all"
                ? "No matching letters"
                : "No letters generated yet"
            }
            subtitle={
              query || filterType !== "all"
                ? "Try a different search or filter."
                : "Generate your first letter from a template."
            }
            action={
              !query &&
              filterType === "all" && (
                <PrimaryButton
                  icon={Plus}
                  onClick={() => navigate(`${basePath}/generateletter/create`)}
                >
                  Create New
                </PrimaryButton>
              )
            }
          />
        ) : (
          <div className="letterTable" style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 760,
              }}
            >
              <thead>
                <tr style={{ background: T.bgHead }}>
                  <th style={th}>Date</th>
                  <th style={th}>Type</th>
                  <th style={th}>Subject</th>
                  <th style={th}>Employee</th>
                  <th style={th}>Status</th>
                  <th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((l) => (
                  <tr key={l.id}>
                    <td style={{ ...td, color: T.fg5, whiteSpace: "nowrap" }}>
                      {formatDate(l.created_at)}
                    </td>
                    <td style={td}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: T.pill,
                          background: T.orange50,
                          color: T.orange,
                          font: `600 11px ${T.font}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {typeLabel(l.template_type)}
                      </span>
                    </td>
                    <td
                      style={{
                        ...td,
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={l.subject}
                    >
                      {l.subject || "—"}
                    </td>
                    <td style={{ ...td, color: T.fg3 }}>{employeeName(l)}</td>
                    <td style={td}>
                      <StatusBadge label={l.status || "generated"} />
                    </td>
                    <td style={td}>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          justifyContent: "flex-end",
                        }}
                      >
                        <IconButton
                          icon={Eye}
                          title="View"
                          onClick={() =>
                            navigate(`${basePath}/generateletter/${l.id}`)
                          }
                        />
                        <IconButton
                          icon={downloadingId === l.id ? Loader2 : Download}
                          title="Download PDF"
                          disabled={downloadingId === l.id}
                          onClick={() => downloadLetter(l)}
                        />
                        <IconButton
                          icon={Pencil}
                          title="Edit & regenerate"
                          onClick={() => editLetter(l)}
                        />
                        <IconButton
                          icon={Trash2}
                          tone="danger"
                          title="Delete"
                          onClick={() => setConfirmDelete(l)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Pagination
        page={safePage}
        totalPages={totalPages}
        total={filtered.length}
        pageSize={PER_PAGE}
        onPage={setPage}
      />

      {confirmDelete && (
        <ModalOverlay onClose={() => !deleting && setConfirmDelete(null)}>
          <Card
            onClick={(e) => e.stopPropagation()}
            style={{ width: 420, maxWidth: "100%", padding: 24 }}
          >
            <div style={{ display: "flex", gap: 14 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: "rgba(231,0,11,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={20} color={T.danger} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    font: `700 16px ${T.font}`,
                    color: T.fg1,
                    marginBottom: 6,
                  }}
                >
                  Delete this letter?
                </div>
                <div style={{ font: `400 13px/1.5 ${T.font}`, color: T.fg5 }}>
                  This permanently removes the record
                  {confirmDelete.subject ? (
                    <>
                      {" "}
                      for{" "}
                      <b style={{ color: T.fg3 }}>{confirmDelete.subject}</b>
                    </>
                  ) : (
                    ""
                  )}{" "}
                  and its PDF file. This can't be undone.
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 22,
              }}
            >
              <SecondaryButton
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                icon={deleting ? Loader2 : Trash2}
                onClick={deleteLetter}
                disabled={deleting}
                style={{ background: T.danger, boxShadow: "none" }}
              >
                {deleting ? "Deleting…" : "Delete"}
              </PrimaryButton>
            </div>
          </Card>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default GenerateLetter;
